import {
	Component,
	computed,
	DestroyRef,
	effect,
	inject,
	input,
	Input,
	OnInit,
	Pipe,
	PipeTransform,
	signal,
	WritableSignal,
	ChangeDetectionStrategy,
	output,
} from '@angular/core';

import { SessionService } from '../../../../../../services/session.service';
import {
	BillingService,
	TenantPayment,
	TenantPaymentService,
	TenantPaymentValue,
	TenantSelection,
	TenantSelectionService,
	TenantSelectionValue,
	TenantSetting,
	TenantSettingService,
	TenantSettingValue,
	TenantShare,
	TenantShareService,
	TenantShareTypeMeta,
	TenantShareValue,
	TenantTarif,
	TenantTarifService,
	TenantTarifValue,
	TenantUnitReference,
	TenantUnitUserReference,
} from '@eadsysteme/billingclient-v1';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Icons } from 'src/app/util/Icons';
import { MatTableDataSource } from '@angular/material/table';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { DateUtil } from '../../../../../../util/DateUtil';
import { fadeInUp400ms } from '../../../../../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../../../../../@vex/animations/stagger.animation';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconButton } from '@angular/material/button';
import dayjs, { Dayjs } from 'dayjs';
import { ParameterValueGanttComponent } from './gantt-chart/parameter-value-gantt.component';
import { BarTextStyle, GanttClickEvent, GanttRow, GanttSegment, ParameterValueType } from '../../../../../util/ara-bar-chart/gantt.models';
import { GanttOverlapService } from '../../../../../../services/gantt-overlap.service';
import { MatIcon } from '@angular/material/icon';
import { BillingHelper } from '../../../../../../services/BillingHelper';
import { ParameterValueDialogComponent } from './parameter-value-dialog/parameter-value-dialog.component';
import { DateRange, TenantUnitDateRangeFilterService } from '../../../../../tenant-unit-detail-legacy/service/tenant-unit-date-range-filter.service';
import { DateRangeFilterDialogComponent } from '../../../../../tenant-unit-detail-legacy/date-range-filter-dialog/date-range-filter-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RealEstate, RealEstateService } from '@eadsysteme/ewsrealestateclient-v3';
import { ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs/operators';

interface ParameterValueDialogOptions {
	type: ParameterValueType;
	isEdit: boolean;
	parameterParentId?: string;
	parameterValueId?: string;
	userId?: string;
	preloadedValue?: any;
}

@Pipe({
	standalone: true,
	name: 'filter',
})
export class FilterPipe implements PipeTransform {
	public constructor() {}

	transform(value: Array<any>, callback: any): any {
		return value.filter(callback);
	}
}

interface HasName {
	name?: string;
}

/** Rolle mit Display-Label und zugehörigen Nutzern */
export interface RoleGroup {
	/** Anzeige-Label (z.B. "Mieter", "Bewohner", "Eigentümer") */
	displayLabel: string;
	/** userRoleType-Schlüssel (TENANT / RESIDENT / OWNER / benutzerdefiniert) */
	roleTypeKey: string;
	/** Alle User-Referenzen dieser Rolle */
	users: TenantUnitUserReference[];
	/** Alle UserRole-IDs (für Payment/Setting-Filterung) */
	userRoleExportIds: Set<string>;
	/** Vorberechnete Gantt-Daten – werden in processChartData() befüllt, nicht im Template berechnet */
	userGanttRows: GanttRow[];
	paymentGanttRows: GanttRow[];
	settingGanttRows: GanttRow[];
}

@Component({
	selector: 'ara-tenant-unit-reference',
	imports: [
		ReactiveFormsModule,
		FormsModule,
		TranslocoDirective,
		ParameterValueGanttComponent,
		MatIcon,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatTooltipModule,
		MatIconButton,
	],
	templateUrl: './tenant-unit-reference.component.html',
	styleUrl: './tenant-unit-reference.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [fadeInUp400ms, stagger40ms],
})
export class TenantUnitReferenceComponent implements OnInit {
	@Input() currentRealEstate: RealEstate | null = null;
	readonly billingId = input<string>(undefined);
	validFromFilter = input<Dayjs>();
	validToFilter = input<Dayjs>();
	selectedInstance = input<TenantUnitReference>();
	canNavigatePrevious = input<boolean>(false);
	canNavigateNext = input<boolean>(false);
	finishedLoading = signal<boolean>(false);

	readonly navigatePrevious = output<void>();
	readonly navigateNext = output<void>();
	readonly dateRangeShift = output<{
		startDate: Date;
		endDate: Date;
	}>();

	private readonly destroyRef = inject(DestroyRef);
	protected dateUtil = inject(DateUtil);
	protected icons = inject(Icons);
	protected ganttOverlapService = inject(GanttOverlapService);
	private readonly sessionService = inject(SessionService);
	private readonly dialog = inject(MatDialog);
	private readonly translocoService = inject(TranslocoService);
	private readonly realEstateService = inject(RealEstateService);
	private readonly activeRoute = inject(ActivatedRoute);
	// TODO(billingclient-v637): TenantParameterService entfernt — Aufruf liefert bis zur Backend-Klärung leere Listen pro Wertart.
	private readonly tenantParameterService = {
		getTenantParameters: (
			_area: number,
			_billingId: string,
			_instanceExportId: string
		): Observable<{
			tenantSelectionValues?: TenantSelectionValue[];
			tenantTarifValues?: TenantTarifValue[];
			tenantShareValues?: TenantShareValue[];
			tenantPaymentValues?: TenantPaymentValue[];
			tenantSettingValues?: TenantSettingValue[];
		}> =>
			of({
				tenantSelectionValues: [],
				tenantTarifValues: [],
				tenantShareValues: [],
				tenantPaymentValues: [],
				tenantSettingValues: [],
			}),
	};
	private readonly tenantShareService = inject(TenantShareService);
	private readonly tenantSelectionService = inject(TenantSelectionService);
	private readonly tenantTarifService = inject(TenantTarifService);
	private readonly tenantPaymentService = inject(TenantPaymentService);
	private readonly tenantSettingService = inject(TenantSettingService);
	private readonly billingHelper = inject(BillingHelper);
	protected readonly tenantUnitDateRangeFilter = inject(TenantUnitDateRangeFilterService);
	private readonly billingService = inject(BillingService);

	protected readonly ParameterValueType = ParameterValueType;
	protected readonly dayjs = dayjs;

	searchFilterSubject: Subject<string> = new Subject<string>();
	searchFilter$: Observable<string> = this.searchFilterSubject.asObservable();
	private readonly tenantShareTypeMeta = TenantShareTypeMeta;
	protected tenantSelectionsDs: MatTableDataSource<TenantSelectionValue>;
	protected tenantTariffsDs: MatTableDataSource<TenantTarifValue>;
	protected tenantSharesDs: MatTableDataSource<TenantShareValue>;
	protected tenantPaymentsDs: MatTableDataSource<TenantPaymentValue>;
	protected tenantSettingsDs: MatTableDataSource<TenantSettingValue>;

	isShowCurrent: WritableSignal<boolean> = signal(false);
	// Flag to disable animation during timeline drag
	isDraggingTimeline: WritableSignal<boolean> = signal(false);

	public startDate = computed(() => {
		const fromFilter = this.tenantUnitDateRangeFilter.validFrom$() ?? this.validFromFilter();
		// Use Day.js to snap to the beginning of the selected month.
		return fromFilter ? fromFilter.startOf('month').toDate() : new Date(new Date().getFullYear(), 0, 1);
	});

	public endDate = computed(() => {
		const toFilter = this.tenantUnitDateRangeFilter.validTo$() ?? this.validToFilter();
		// Use Day.js to snap to the very end of the selected month.
		return toFilter ? toFilter.endOf('month').toDate() : new Date(new Date().getFullYear(), 11, 31);
	});

	// Gantt chart layout constants (single source of truth for template)
	protected readonly GANTT_ROW_HEIGHT_MAIN = 50;
	protected readonly GANTT_ROW_HEIGHT = 40;
	protected readonly GANTT_BAR_HEIGHT = 26;
	protected readonly GANTT_AXIS_OFFSET = 12;

	public myBarTextStyle: BarTextStyle = {
		color: 'black',
		fontSize: '12px',
		fontWeight: 'bold',
	};

	ganttDataGeneral: GanttRow[] = [];
	processedGanttDataShare: GanttRow[] = [];
	processedGanttDataExemption: GanttRow[] = [];
	processedGanttDataFlatrate: GanttRow[] = [];
	processedGanttDataTariff: GanttRow[] = [];

	/** Alle Rollengruppen (TENANT, RESIDENT, OWNER, ...) mit ihren Nutzern und Gantt-Daten */
	roleGroups: RoleGroup[] = [];

	/** Collapsible role sections – key ist roleTypeKey */
	expandedRoles: WritableSignal<Set<string>> = signal(new Set<string>());

	// Available parameters for the billing (loaded once)
	private availableShares: TenantShare[] = [];
	private availableSelections: TenantSelection[] = [];
	private availableTariffs: TenantTarif[] = [];
	private availablePayments: TenantPayment[] = [];
	private availableSettings: TenantSetting[] = [];

	/** Alle Payment-Values aus der API (ungefiltert) */
	private allPaymentValues: TenantPaymentValue[] = [];
	/** Alle Setting-Values aus der API (ungefiltert) */
	private allSettingValues: TenantSettingValue[] = [];
	/** Roh-Setting-Values (vor Übersetzung) – für den Edit-Dialog */
	private rawSettingValues: TenantSettingValue[] = [];

	/** Konfigurations-Map: leitet hasValue/hasOptionValue/isUserRelated vom ParameterValueType ab */
	private readonly parameterTypeConfig: Partial<Record<ParameterValueType, { hasValue: boolean; hasOptionValue: boolean; isUserRelated: boolean }>> = {
		[ParameterValueType.SHARE]: { hasValue: true, hasOptionValue: false, isUserRelated: false },
		[ParameterValueType.SELECTION]: { hasValue: false, hasOptionValue: false, isUserRelated: false },
		[ParameterValueType.TARIFF]: { hasValue: true, hasOptionValue: false, isUserRelated: false },
		[ParameterValueType.ROLE]: { hasValue: false, hasOptionValue: false, isUserRelated: false },
		[ParameterValueType.PAYMENT]: { hasValue: true, hasOptionValue: false, isUserRelated: true },
		[ParameterValueType.SETTING]: { hasValue: false, hasOptionValue: true, isUserRelated: true },
	};

	lockPortalAccessUntil: dayjs.Dayjs = null;

	constructor() {
		effect(() => {
			const instance = this.selectedInstance();
			if (instance) {
				this.fillTables();
			}
		});
	}

	ngOnInit() {
		if (this.currentRealEstate) {
			this.tenantUnitDateRangeFilter.realEstate = this.currentRealEstate;
		} else {
			const currentRealEstateId = this.activeRoute.snapshot.paramMap.get('realEstateId');
			const currentArea = this.sessionService.effectiveArea;

			if (currentRealEstateId) {
				this.finishedLoading.set(false);
				this.realEstateService
					.getRealEstate(currentArea, currentRealEstateId)
					.pipe(
						takeUntilDestroyed(this.destroyRef),
						finalize(() => this.finishedLoading.set(true))
					)
					.subscribe({
						next: (realEstate) => {
							this.currentRealEstate = realEstate;
							this.tenantUnitDateRangeFilter.realEstate = this.currentRealEstate;
						},
					});
			}
		}

		const billingId = this.billingId();
		if (billingId) {
			this.billingHelper.syncBilling(this.sessionService.effectiveArea, billingId);
			this.loadPortalAccessUntil();
		}
	}

	toggleRoleSection(roleTypeKey: string): void {
		const current = this.expandedRoles();
		const newSet = new Set(current);
		if (newSet.has(roleTypeKey)) {
			newSet.delete(roleTypeKey);
		} else {
			newSet.add(roleTypeKey);
		}
		this.expandedRoles.set(newSet);
	}

	isRoleExpanded(roleTypeKey: string): boolean {
		return this.expandedRoles().has(roleTypeKey);
	}

	/** Vorberechnete Gantt-Rows für Nutzer einer Rollengruppe.
	 *  Alle Nutzer der Rolle landen als Segmente in EINER Zeile (kein Label, Name steht im Bar).
	 */
	private buildUserGanttForGroup(group: RoleGroup): GanttRow[] {
		if (!group.users.length) return [];
		const segments: GanttSegment[] = group.users.map((user) => ({
			from: new Date(user.validFrom ?? DateUtil.minDate.toDate()),
			to: new Date(user.validTo ?? DateUtil.maxDate.toDate()),
			value: user.name ?? '',
			color: 'rgba(var(--color-primary), 0.08)',
			additionalData: user,
			additionalDataType: 'userRole',
		}));
		return [{ label: '', segments, overlaps: [] }];
	}

	/** Vorberechnete Gantt-Rows für Zahlungen einer Rollengruppe */
	private buildPaymentGanttForGroup(group: RoleGroup): GanttRow[] {
		const relevantPayments = this.availablePayments.filter((p) => p.validUserRoleExportId && group.userRoleExportIds.has(p.validUserRoleExportId));
		const relevantPaymentExportIds = new Set(relevantPayments.map((p) => p.exportId));
		const filteredValues = this.allPaymentValues.filter((pv) => pv.tenantPaymentExportId && relevantPaymentExportIds.has(pv.tenantPaymentExportId));
		const valuesByPayment = this.createEntityMap<TenantPayment, TenantPaymentValue>(filteredValues, relevantPayments, 'tenantPaymentExportId');
		return this.processGanttDataWithAllParameters(valuesByPayment, relevantPayments, 'tenantPayment');
	}

	private buildSettingGanttForGroup(group: RoleGroup): GanttRow[] {
		const relevantSettings = this.availableSettings.filter((s) => s.validUserRoleExportId && group.userRoleExportIds.has(s.validUserRoleExportId));
		const relevantSettingExportIds = new Set(relevantSettings.map((s) => s.exportId));
		const filteredValues = this.allSettingValues.filter((sv) => sv.tenantSettingExportId && relevantSettingExportIds.has(sv.tenantSettingExportId));
		const valuesBySetting = this.createEntityMap<TenantSetting, TenantSettingValue>(filteredValues, relevantSettings, 'tenantSettingExportId');
		return this.processGanttDataWithAllParameters(valuesBySetting, relevantSettings, 'tenantSetting');
	}

	/**
	 * Gruppiert alle tenantUnitUserReferences nach userRoleType (TENANT / RESIDENT / OWNER).
	 * Berechnet Gantt-Daten direkt mit ein — damit im Template keine Methoden mehr aufgerufen werden.
	 */
	private buildRoleGroups(tenantUnitReferenceDetail: TenantUnitReference): RoleGroup[] {
		const refs = tenantUnitReferenceDetail.tenantUnitUserReferences ?? [];
		const groupMap = new Map<string, RoleGroup>();

		for (const user of refs) {
			const roleType = user.userRole?.userRoleType ?? 'UNKNOWN';
			if (!groupMap.has(roleType)) {
				const nameFromRole = user.userRole?.name?.trim();
				const translatedRoleType = this.translocoService.translate('enum.' + roleType);
				const displayLabel = nameFromRole || (translatedRoleType === 'enum.' + roleType ? roleType : translatedRoleType);
				groupMap.set(roleType, {
					displayLabel,
					roleTypeKey: roleType,
					users: [],
					userRoleExportIds: new Set<string>(),
					userGanttRows: [],
					paymentGanttRows: [],
					settingGanttRows: [],
				});
			}
			const group = groupMap.get(roleType);
			group.users.push(user);
			if (user.userRole?.exportId) {
				group.userRoleExportIds.add(user.userRole.exportId);
			}
		}

		// Reihenfolge: TENANT → RESIDENT → OWNER → andere
		const order = ['TENANT', 'RESIDENT', 'OWNER'];
		const result: RoleGroup[] = [];
		for (const key of order) {
			if (groupMap.has(key)) result.push(groupMap.get(key));
			groupMap.delete(key);
		}
		for (const group of groupMap.values()) result.push(group);

		// Gantt-Daten vorberechnen (einmalig, nicht bei jeder CD)
		for (const group of result) {
			group.userGanttRows = this.buildUserGanttForGroup(group);
			group.paymentGanttRows = this.buildPaymentGanttForGroup(group);
			group.settingGanttRows = this.buildSettingGanttForGroup(group);
		}

		return result;
	}

	private createEntityMap<TEntity extends HasName & { exportId?: string }, TValue extends { exportId?: string }>(
		values: TValue[],
		entities: TEntity[],
		valueRefIdKey: keyof TValue
	): Map<string, { entity: TEntity; values: TValue[] }> {
		const entityById = new Map<string, TEntity>();
		for (const entity of entities ?? []) {
			if (entity.exportId) {
				entityById.set(entity.exportId, entity);
			}
		}

		const map = new Map<string, { entity: TEntity; values: TValue[] }>();
		for (const value of values ?? []) {
			const refId = value[valueRefIdKey] as string | undefined;
			if (!refId) continue;
			const entity = entityById.get(refId);
			if (!entity?.name) continue;

			const existing = map.get(entity.name);
			if (existing) {
				existing.values.push(value);
			} else {
				map.set(entity.name, { entity, values: [value] });
			}
		}

		return map;
	}

	private processChartData(): void {
		const sharePropertyKey = 'tenantShare';
		const exemptionPropertyKey = 'tenantSelection';
		const tariffPropertyKey = 'tenantTarif';

		const valuesByShare = this.createEntityMap<TenantShare, TenantShareValue>(this.tenantSharesDs?.data, this.availableShares, 'tenantShareExportId');
		const valuesByExemption = this.createEntityMap<TenantSelection, TenantSelectionValue>(
			this.tenantSelectionsDs?.data,
			this.availableSelections,
			'tenantSelectionExportId'
		);
		const valuesByTariff = this.createEntityMap<TenantTarif, TenantTarifValue>(this.tenantTariffsDs?.data, this.availableTariffs, 'tenantTarifExportId');

		/***
         the chart has to be the following "order"
         first row: TenantUnit as Bar
         rows afterwards: Share, Exemption, Flatrate, Tariff as rows with Values as Bars
         row: current Tenants/Residents: with each tenant with validFrom-To as Bar
         afterwards: Payment, Setting as rows, with Values as Bars (per Rolle gefiltert)
         ***/

		this.ganttDataGeneral = [
			{
				label: '', // Empty label to maintain spacing alignment
				segments: [
					{
						from: new Date(this.selectedInstance().validFrom ?? DateUtil.minDate.toDate()),
						to: new Date(this.selectedInstance().validTo ?? DateUtil.maxDate.toDate()),
						value: this.selectedInstance().name ?? '',
						color: 'rgba(var(--color-primary), 1)',
						additionalData: this.selectedInstance(),
					},
				],
			},
		];
		// Process gantt data with all available parameters (show rows even without values)
		this.processedGanttDataShare = this.processGanttDataWithAllParameters(valuesByShare, this.availableShares, sharePropertyKey);
		this.processedGanttDataExemption = this.processGanttDataWithAllParameters(valuesByExemption, this.availableSelections, exemptionPropertyKey);
		this.processedGanttDataTariff = this.processGanttDataWithAllParameters(valuesByTariff, this.availableTariffs, tariffPropertyKey);

		// Rollengruppen aufbauen (TENANT, RESIDENT, OWNER, ...)
		this.roleGroups = this.buildRoleGroups(this.selectedInstance());

		// Expand all role sections by default
		this.expandedRoles.set(new Set(this.roleGroups.map((g) => g.roleTypeKey)));
	}

	/**
	 * Creates gantt data for all available parameters, including those without values.
	 * This ensures that every parameter type is shown as a row, even if empty.
	 */
	private processGanttDataWithAllParameters<T extends { name?: string; exportId?: string; type?: any }>(
		valuesByParameter: Map<string, { entity: T; values: any[] }>,
		availableParameters: T[],
		dataObjectKey: string,
		valueField: string = 'value'
	): GanttRow[] {
		const ganttData: GanttRow[] = [];
		const processedNames = new Set<string>();

		for (const entry of valuesByParameter.entries()) {
			const key = entry[0];
			const { entity, values } = entry[1];
			processedNames.add(key);

			const unit = this.tenantShareTypeMeta[entity?.type]?.unit;
			const displayUnit = unit && !['UNDEFINED', 'UNKNOWN'].includes(unit) ? unit : undefined;

			const segments: GanttSegment[] = values.map((value) => {
				const fromDate = new Date(value.validFrom ?? DateUtil.minDate.toDate());
				const toDate = new Date(value.validTo ?? DateUtil.maxDate.toDate());

				return {
					from: fromDate,
					to: toDate,
					valueId: value.exportId,
					value: value[valueField] ?? '',
					color: 'rgba(var(--color-primary), 0.08)',
					additionalData: entity,
					additionalDataType: dataObjectKey,
					unit: displayUnit,
				} as GanttSegment;
			});

			ganttData.push({
				label: key,
				typeId: entity?.exportId,
				segments: segments,
			});
		}

		for (const param of availableParameters) {
			if (param.name && !processedNames.has(param.name)) {
				ganttData.push({
					label: param.name,
					typeId: param.exportId,
					segments: [],
				});
			}
		}

		return ganttData.map((row) => {
			const overlapSegments = this.ganttOverlapService.calculateOverlapSegments(row.segments);
			return {
				...row,
				overlaps: overlapSegments,
			};
		});
	}
	private fromTypeGroupName(input: string): string {
		return input
			.replace(/Values$/, '')
			.replaceAll(/[A-Z]/g, (letter, index) => (index === 0 ? letter : '_' + letter))
			.toUpperCase();
	}

	private fillTables() {
		// Load available parameters for billing and parameter values for selected instance
		forkJoin({
			tenantParameters: this.tenantParameterService.getTenantParameters(
				this.sessionService.effectiveArea,
				this.billingId(),
				this.selectedInstance().exportId
			),
			shares: this.tenantShareService.readAllTenantShare(this.sessionService.effectiveArea, this.billingId()),
			selections: this.tenantSelectionService.readAllTenantSelection(this.sessionService.effectiveArea, this.billingId()),
			tariffs: this.tenantTarifService.readAllTenantTarif(this.sessionService.effectiveArea, this.billingId()),
			payments: this.tenantPaymentService.readAllTenantPayment(this.sessionService.effectiveArea, this.billingId()),
			settings: this.tenantSettingService.readAllTenantSetting(this.sessionService.effectiveArea, this.billingId()),
		})
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: ({ tenantParameters, shares, selections, tariffs, payments, settings }) => {
					// Store available parameters
					this.availableShares = shares ?? [];
					this.availableSelections = selections ?? [];
					this.availableTariffs = tariffs ?? [];
					this.availablePayments = payments ?? [];
					this.availableSettings = settings ?? [];

					this.tenantSelectionsDs = new MatTableDataSource(tenantParameters.tenantSelectionValues ?? []);
					this.tenantTariffsDs = new MatTableDataSource(tenantParameters.tenantTarifValues ?? []);
					this.tenantSharesDs = new MatTableDataSource(tenantParameters.tenantShareValues ?? []);

					// Payment- und Setting-Values ungefiltert speichern (Filterung erfolgt pro Rolle)
					this.allPaymentValues = tenantParameters.tenantPaymentValues ?? [];
					this.rawSettingValues = tenantParameters.tenantSettingValues ?? [];
					this.allSettingValues = tenantParameters.tenantSettingValues ?? [];
					this.tenantPaymentsDs = new MatTableDataSource(this.allPaymentValues);

					const processedSettingValues = (tenantParameters.tenantSettingValues ?? []).map((tenantSettingValue: TenantSettingValue) => {
						// TODO ugly workaround - fix BE / API / interface
						const tsvValue = tenantSettingValue.value as any;
						if (tsvValue instanceof Object && tsvValue.typeGroup && tsvValue.typeValue) {
							// Format: "Lohnanteil ausweisen: Ja ausweisen" (with Ja/Nein prefix)
							const typeGroupTranslation = this.translocoService.translate(
								'billing.ordinaryAllocationType.' + this.fromTypeGroupName(tsvValue.typeGroup)
							);
							const yesNoPrefix = this.translocoService.translate('common.' + tsvValue.typeValue.toLowerCase());
							const valueTranslation = this.translocoService.translate('tenantUnitSettingValue.' + tsvValue.typeValue);
							return { ...tenantSettingValue, value: `${typeGroupTranslation}: ${yesNoPrefix} ${valueTranslation}` as never };
						} else {
							return {
								...tenantSettingValue,
								value: this.translocoService.translate('common.' + tenantSettingValue.value.toString().toLowerCase()) as any,
							};
						}
					});
					this.allSettingValues = processedSettingValues;
					this.tenantSettingsDs = new MatTableDataSource(processedSettingValues);

					this.tenantSelectionsDs.filterPredicate = this.currentFilterPredicate.bind(this);
					this.tenantTariffsDs.filterPredicate = this.currentFilterPredicate.bind(this);
					this.tenantSharesDs.filterPredicate = this.currentFilterPredicate.bind(this);
					this.tenantPaymentsDs.filterPredicate = this.currentFilterPredicate.bind(this);
					this.tenantSettingsDs.filterPredicate = this.currentFilterPredicate.bind(this);

					this.processChartData();
				},
			});
	}

	private loadPortalAccessUntil() {
		this.billingService
			.readBilling(this.sessionService.effectiveArea, this.billingId())
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((billing) => {
				this.lockPortalAccessUntil = dayjs(billing.lockPortalAccessUntil);
			});
	}

	currentFilterPredicate(data: TenantSelectionValue, filter: string): boolean {
		if (filter === 'valid') {
			return this.isShowCurrent();
		}
		return true; // Wenn kein Filter aktiv, alle anzeigen
	}

	/** Öffnet den Dialog zum Anlegen oder Bearbeiten eines Parameterwerts. */
	private openParameterValueDialog(options: ParameterValueDialogOptions): void {
		const config = this.parameterTypeConfig[options.type];
		if (!config) return;

		const matDialogRef = this.dialog.open(ParameterValueDialogComponent, {
			disableClose: true,
			width: '670px',
			data: {
				billingId: this.billingId(),
				currentParameter: options.type,
				billingInstance: this.selectedInstance(),
				hasValue: config.hasValue,
				hasOptionValue: config.hasOptionValue,
				isUserRelated: config.isUserRelated,
				isEdit: options.isEdit,
				parameterParentId: options.parameterParentId,
				parameterValueId: options.parameterValueId,
				userId: options.userId,
				preloadedValue: options.preloadedValue,
				users: config.isUserRelated ? this.selectedInstance().tenantUnitUserReferences : [],
				lockPortalAccessUntil: this.lockPortalAccessUntil,
			},
		});

		matDialogRef.afterClosed().subscribe(() => this.fillTables());
	}

	/** Add-Button geklickt: neuen Parameterwert anlegen (Typ bestimmt die Dialog-Konfiguration). */
	addParameterValueByType(clickEvent: GanttClickEvent): void {
		this.openParameterValueDialog({ type: clickEvent.parameterValueType, isEdit: false, parameterParentId: clickEvent.row?.typeId });
	}

	/** Segment geklickt: bestehenden Parameterwert bearbeiten. */
	editParameterValueBySegment(clickEvent: GanttClickEvent, group?: RoleGroup): void {
		const type = clickEvent.parameterValueType;

		switch (type) {
			case ParameterValueType.PAYMENT:
			case ParameterValueType.SETTING: {
				const values = type === ParameterValueType.PAYMENT ? this.allPaymentValues : this.rawSettingValues;
				const value = values.find((v) => v.exportId === clickEvent.segment?.valueId);
				// TODO(billingclient-v637): userId existiert nicht mehr auf Payment-/SettingValue — Fallback über aktuelle Gruppe.
				const userId = (value as { userId?: string })?.userId ?? (group?.users.length === 1 ? group.users[0].exportId : undefined);
				this.openParameterValueDialog({
					type,
					isEdit: true,
					parameterParentId: clickEvent.row.typeId,
					parameterValueId: clickEvent.segment?.valueId,
					userId,
					preloadedValue: value,
				});
				break;
			}
			default:
				this.openParameterValueDialog({ type, isEdit: true, parameterParentId: clickEvent.row.typeId, parameterValueId: clickEvent.segment?.valueId });
				break;
		}
	}

	handleDateRangeShift(event: { startDate: Date; endDate: Date }): void {
		// Snap to month boundaries for consistent display
		const newStart = dayjs(event.startDate).startOf('month');
		const newEnd = dayjs(event.endDate).endOf('month');

		// Update the date range filter service (this will update the datepickers above the tabs)
		this.tenantUnitDateRangeFilter.setDateRange(newStart, newEnd);
		this.dateRangeShift.emit(event);
	}

	onDragStart(): void {
		this.isDraggingTimeline.set(true);
	}

	onDragEnd(): void {
		// Keep animation disabled for a short time after drag ends
		// to prevent animation from triggering on the final render
		setTimeout(() => {
			this.isDraggingTimeline.set(false);
		}, 100);
	}

	openDateRangeFilterDialog() {
		const dialogRef = this.dialog.open(DateRangeFilterDialogComponent, {
			width: '800px',
			data: {
				validFrom: this.tenantUnitDateRangeFilter.validFrom,
				validTo: this.tenantUnitDateRangeFilter.validTo,
				realEstate: this.tenantUnitDateRangeFilter.realEstate,
			},
			autoFocus: false,
			restoreFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result: DateRange) => {
				if (result) {
					this.tenantUnitDateRangeFilter.setDateRange(result.validFrom, result.validTo);
				}
			});
	}
}
